import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Shield, Users, FileText, ChevronLeft, ChevronRight, Quote, Lock } from 'lucide-react';
import MFAVerificationModal from '../components/MFAVerificationModal';

// Carousel slides with images and quotes
const CAROUSEL_SLIDES = [
  {
    url: 'https://customer-assets.emergentagent.com/job_clinidocs-3/artifacts/nrmk0gof_AdobeStock_572971350.jpeg',
    alt: 'AI-Powered Clinical Intelligence',
    quote: 'Accelerate your clinical research with precision, compliance, and innovation. Your breakthrough is our mission.'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_clinidocs-3/artifacts/qxelh1to_AdobeStock_472181971.jpeg',
    alt: 'Secure Document Management',
    quote: 'Transforming clinical trials with scientific expertise and operational excellence—because every study matters.'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_clinidocs-3/artifacts/u0p51t1y_AdobeStock_294839451.jpeg',
    alt: 'Data-Driven Excellence',
    quote: 'From concept to cure, we navigate the complexities of clinical research so you can focus on innovation.'
  }
];

// eTMF Vault Logo URL (icon only from original)
const ETMF_VAULT_LOGO = "https://www.vetforcesolutions.com/backend/uploads/8e6f835bdae1ff1ee6c1d6b663d984dc.png";

// Custom Logo Component with white eTMF text
const ETMFVaultLogo = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: { icon: 'h-6 w-6', text: 'text-lg', gap: 'gap-1.5' },
    md: { icon: 'h-8 w-8', text: 'text-2xl', gap: 'gap-2' },
    lg: { icon: 'h-10 w-10', text: 'text-3xl', gap: 'gap-2.5' },
    xl: { icon: 'h-12 w-12', text: 'text-4xl', gap: 'gap-3' },
    '2xl': { icon: 'h-14 w-14', text: 'text-5xl', gap: 'gap-3' }
  };
  const s = sizes[size] || sizes.md;
  
  return (
    <div className={`flex items-center ${s.gap} ${className}`}>
      {/* Shield/Lock Icon */}
      <div className={`${s.icon} relative flex items-center justify-center`}>
        <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
          <path d="M12 2L4 6v6c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V6l-8-4z" 
                fill="url(#shieldGradient)" stroke="#60A5FA" strokeWidth="0.5"/>
          <path d="M9 12l2 2 4-4" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <defs>
            <linearGradient id="shieldGradient" x1="4" y1="2" x2="20" y2="20">
              <stop offset="0%" stopColor="#3B82F6"/>
              <stop offset="100%" stopColor="#1E40AF"/>
            </linearGradient>
          </defs>
        </svg>
      </div>
      {/* Text */}
      <div className={`${s.text} font-bold tracking-tight flex`} style={{ fontFamily: 'Manrope, sans-serif' }}>
        <span className="text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">eTMF</span>
        <span className="text-orange-500 drop-shadow-[0_0_10px_rgba(249,115,22,0.4)]">Vault</span>
      </div>
    </div>
  );
};

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  
  // MFA state
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [mfaData, setMfaData] = useState(null);

  const { login, register, completeMfaLogin } = useAuth();
  const navigate = useNavigate();

  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % CAROUSEL_SLIDES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % CAROUSEL_SLIDES.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + CAROUSEL_SLIDES.length) % CAROUSEL_SLIDES.length);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        
        // Check if MFA is required
        if (result.mfa_required) {
          setMfaData(result);
          setShowMfaModal(true);
          setLoading(false);
          return;
        }
        
        toast.success('Welcome back!');
        navigate('/dashboard');
      } else {
        await register(formData.name, formData.email, formData.password);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'An error occurred';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSuccess = (data) => {
    completeMfaLogin(data);
    setShowMfaModal(false);
    setMfaData(null);
    toast.success('Welcome back!');
    navigate('/dashboard');
  };

  const handleMfaClose = () => {
    setShowMfaModal(false);
    setMfaData(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Full Screen Image Carousel Background */}
      {CAROUSEL_SLIDES.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentImageIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.url}
            alt={slide.alt}
            className="w-full h-full object-cover"
            loading={index === 0 ? "eager" : "lazy"}
          />
        </div>
      ))}

      {/* Dark Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-slate-900/70 to-blue-900/60" />

      {/* Content Container */}
      <div className="relative z-10 min-h-screen flex flex-col safe-area-inset-top safe-area-inset-bottom">
        {/* Top Bar - Logo and Branding */}
        <header className="p-3 sm:p-4 md:p-6 lg:p-8">
          <div className="flex items-center justify-between">
            
            <img src={ETMF_VAULT_LOGO} alt="ETMF Vault Logo" className="h-10 sm:h-12 md:h-14 lg:h-16" />
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 flex items-center justify-center px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
          <div className="w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-6 lg:gap-16 items-center">
            
            {/* Left Side - Branding & Quote (Hidden on mobile) */}
            <div className="hidden lg:block space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl xl:text-5xl 2xl:text-6xl font-bold text-white leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Service to Science<br />& Innovation
                </h1>
                <p className="text-lg xl:text-xl text-white/80">
                  Enterprise eTMF Solution. Securely manage your clinical documents.
                </p>
              </div>

              {/* Dynamic Quote Section */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 xl:p-6 border border-white/20">
                <Quote className="w-6 h-6 xl:w-8 xl:h-8 text-blue-400 mb-3 opacity-70" />
                <p className="text-base xl:text-lg text-white/90 italic leading-relaxed">
                  &ldquo;{CAROUSEL_SLIDES[currentImageIndex].quote}&rdquo;
                </p>
              </div>

              {/* Carousel Controls */}
              <div className="flex items-center gap-4">
                <button
                  onClick={prevImage}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors touch-target"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-5 h-5 xl:w-6 xl:h-6" />
                </button>
                <div className="flex gap-2">
                  {CAROUSEL_SLIDES.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      aria-label={`Go to slide ${index + 1}`}
                      className={`h-2 rounded-full transition-all ${
                        index === currentImageIndex 
                          ? 'bg-blue-500 w-8' 
                          : 'bg-white/30 hover:bg-white/50 w-2'
                      }`}
                    />
                  ))}
                </div>
                <button
                  onClick={nextImage}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Right Side - Login Panel */}
            <div className="w-full max-w-md mx-auto lg:mx-0">
              {/* Glass Card Login Panel */}
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 p-5 sm:p-6 md:p-8 lg:p-10">
                {/* Lock Icon */}
                <div className="flex justify-center mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-blue-500 to-teal-500 flex items-center justify-center shadow-lg">
                    <Lock className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
                  </div>
                </div>

                {/* Header */}
                <div className="text-center mb-5 sm:mb-6 md:mb-8">
                  <h2 className="text-2xl sm:text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {isLogin ? 'Welcome Back' : 'Create Account'}
                  </h2>
                  <p className="text-white/70 mt-1.5 sm:mt-2 text-sm sm:text-base">
                    {isLogin ? 'Sign in to access your clinical documents' : 'Join eTMF Vault today'}
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                  {!isLogin && (
                    <div className="space-y-1.5 sm:space-y-2 animate-fade-in">
                      <Label htmlFor="name" className="text-white/90 font-medium text-sm sm:text-base">Full Name</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={handleChange}
                        required={!isLogin}
                        data-testid="register-name-input"
                        className="h-11 sm:h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/20 focus:border-blue-400 transition-all text-base"
                      />
                    </div>
                  )}

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="email" className="text-white/90 font-medium text-sm sm:text-base">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="login-email-input"
                      className="h-11 sm:h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/20 focus:border-blue-400 transition-all text-base"
                    />
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="password" className="text-white/90 font-medium text-sm sm:text-base">Password</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={6}
                      data-testid="login-password-input"
                      className="h-11 sm:h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/20 focus:border-blue-400 transition-all text-base"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="login-submit-btn"
                    className="w-full h-11 sm:h-12 rounded-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white font-semibold text-base sm:text-lg shadow-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] touch-target"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : isLogin ? (
                      'Sign In'
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </form>

                {/* Toggle Auth Mode */}
                <div className="mt-4 sm:mt-6 text-center">
                  <button
                    type="button"
                    data-testid="toggle-auth-mode-btn"
                    className="text-xs sm:text-sm text-blue-300 hover:text-blue-200 font-medium transition-colors"
                  >
                  </button>
                </div>
              </div>

              {/* Terms */}
              <p className="text-center text-xs sm:text-sm text-white/50 mt-4 sm:mt-6 px-4">
                By continuing, you agree to our Terms of Service and Privacy Policy.
              </p>
            </div>
          </div>
        </main>

        {/* Bottom Feature Bar */}
        <footer className="p-3 sm:p-4 md:p-6 lg:p-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-3 gap-2 sm:gap-4 lg:gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-2 sm:p-4 lg:p-5 border border-white/10 text-center">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-blue-400 mx-auto mb-1 sm:mb-2" />
                <h3 className="font-semibold text-white text-xs sm:text-sm lg:text-base">21 CFR Part 11</h3>
                <p className="text-[10px] sm:text-xs lg:text-sm text-white/60 hidden sm:block">Compliant e-signatures</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-2 sm:p-4 lg:p-5 border border-white/10 text-center">
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-teal-400 mx-auto mb-1 sm:mb-2" />
                <h3 className="font-semibold text-white text-xs sm:text-sm lg:text-base">Document Control</h3>
                <p className="text-[10px] sm:text-xs lg:text-sm text-white/60 hidden sm:block">Version management</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-2 sm:p-4 lg:p-5 border border-white/10 text-center">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-purple-400 mx-auto mb-1 sm:mb-2" />
                <h3 className="font-semibold text-white text-xs sm:text-sm lg:text-base">Team Access</h3>
                <p className="text-[10px] sm:text-xs lg:text-sm text-white/60 hidden sm:block">Role-based permissions</p>
              </div>
            </div>

            {/* Footer Text */}
            <div className="text-center mt-3 sm:mt-4 md:mt-6">
              <span className="text-[10px] sm:text-xs text-white/40">
                eTMF Vault •  A Product of <a href="https://www.vetforcesolutions.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Vetforce Solutions</a>
              </span>
            </div>
          </div>
        </footer>
      </div>

      {/* MFA Verification Modal */}
      <MFAVerificationModal
        isOpen={showMfaModal}
        onClose={handleMfaClose}
        mfaData={mfaData}
        onSuccess={handleMfaSuccess}
      />
    </div>
  );
}
